// "Report a problem": a plain-text upload report a person can copy and send to support. What support needs to
// tell why a batch failed: the browser, OS and this build, and per batch its pass, counts, bytes, times and the
// failure reasons (failReasons.ts). Never file names, folder names or paths; galleries by an 8-character id.
import { toast } from "sonner";
import type { UploadJob } from "@/hooks/useUploadManager";
import { clientInfo, failLabel, topReasons, type ClientInfo } from "./failReasons";
import { RESIZE_CONCURRENCY, RESIZE_PARALLEL } from "@/lib/image/resizeImage";

const fmtTime = (ms?: number) => (ms ? new Date(ms).toISOString().replace("T", " ").slice(0, 19) + " UTC" : "—");
const fmtMB = (b: number) => `${(Math.max(0, b) / 1e6).toLocaleString("en-GB", { maximumFractionDigits: 1 })} MB`;

/** The app build this page runs (its hashed bundle's name), so support knows which code it was. */
const buildId = (): string => {
  try {
    return new URL(import.meta.url).pathname.split("/").pop() || "dev";
  } catch {
    return "unknown";
  }
};

export function buildUploadDiagnostics(jobs: UploadJob[], client: ClientInfo = clientInfo(), now = Date.now()): string {
  const lines: string[] = [];
  lines.push(`Photohawk upload report — ${fmtTime(now)}`);
  lines.push(`Browser: ${client.browser} on ${client.os}${client.cores ? ` · ${client.cores} cores` : ""}${client.memoryGB ? ` · ${client.memoryGB} GB` : ""}`);
  lines.push(`User agent: ${client.ua}`);
  lines.push(`Build: ${buildId()} · preview copies: ${RESIZE_PARALLEL} workers, ${RESIZE_CONCURRENCY} at once`);
  const online = typeof navigator !== "undefined" ? navigator.onLine : true;
  lines.push(`Online: ${online ? "yes" : "no"}`);
  const sorted = [...jobs].sort((a, b) => b.startedAt - a.startedAt).slice(0, 20);
  lines.push(`Batches (${sorted.length}, newest first):`);
  for (const j of sorted) {
    const pass = j.previewFirst === "copy" ? "previews" : j.previewFirst === "original" ? "originals (after previews)" : j.resize ? "resized" : "originals";
    lines.push(
      `- ${j.id} · gallery ${j.engineGuid.slice(0, 8)} · ${j.kind} · ${pass}${j.originalsBy ? " (originals later)" : ""} · ${j.phase}` +
        ` · ${j.total.toLocaleString("en-GB")} files: ${j.completed.toLocaleString("en-GB")} done, ${j.failed.toLocaleString("en-GB")} failed` +
        ` · sent ${fmtMB(j.bytesSent)} of ${fmtMB(j.bytesTotal)} · ${fmtTime(j.startedAt)} → ${fmtTime(j.finishedAt)}`,
    );
    for (const [k, n] of topReasons(j.failReasons)) lines.push(`    ${n.toLocaleString("en-GB")} × ${k} — ${failLabel(k, client.browser)}`);
  }
  return lines.join("\n");
}

async function writeClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Older Safari / no permission: a selected textarea and the copy command.
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

/** Copy the report (a click handler: the clipboard needs the person's click). Says whether it worked. */
export async function copyUploadDiagnostics(jobs: UploadJob[]): Promise<boolean> {
  const text = buildUploadDiagnostics(jobs);
  const ok = await writeClipboard(text);
  if (ok) toast.success("Upload report copied", { description: "Paste it into your email to support. It has no file names in it." });
  else toast.error("Couldn't copy the upload report", { description: text.slice(0, 600) });
  return ok;
}
